import React, { useState, useMemo, useCallback, memo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Card, 
  Typography, 
  Upload, 
  Button, 
  Table, 
  Tag, 
  Space, 
  Statistic, 
  Row, 
  Col, 
  Alert, 
  Spin,
  Select,
  Input,
  message,
  Modal,
  Progress
} from 'antd';
import { 
  UploadOutlined, 
  ReloadOutlined, 
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { comparisonAPI, groupsAPI } from '../services/api';
import Loader from '../components/Loader';
import './Comparison.css';

const { Title, Text } = Typography;
const { Search } = Input;

// Custom hook for debounced value - memoized
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface Group {
  _id: string;
  name: string;
  description: string;
  accountId: {
    _id: string;
    accountName: string;
  };
}

interface ComparisonData {
  _id: string;
  groupId: {
    _id: string;
    name: string;
    description: string;
  };
  accountId: {
    _id: string;
    accountName: string;
  };
  uploadedNumbers: Array<{
    phoneNumber: string;
    name: string;
    status: 'present' | 'not_present';
  }>;
  totalUploaded: number;
  presentCount: number;
  notPresentCount: number;
  matchPercentage: number;
  lastAnalyzed: string;
  fileName: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// Memoized status tag component to prevent unnecessary re-renders
const StatusTag = memo(({ status }: { status: 'present' | 'not_present' }) => (
  <Tag 
    color={status === 'present' ? 'green' : 'red'} 
    icon={status === 'present' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
  >
    {status === 'present' ? 'Present' : 'Not Present'}
  </Tag>
));
StatusTag.displayName = 'StatusTag';

const Comparison: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);
  
  // Group search for Select dropdown
  const [groupSearchText, setGroupSearchText] = useState('');
  const debouncedGroupSearchText = useDebounce(groupSearchText, 300);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [statusFilter, setStatusFilter] = useState<'present' | 'not_present' | undefined>(undefined);

  // Memoized query keys
  const groupsQueryKey = useMemo(() => ['groups', 'minimal', debouncedGroupSearchText], [debouncedGroupSearchText]);
  const comparisonQueryKey = useMemo(() => [
    'comparison', 
    selectedGroupId, 
    currentPage, 
    pageSize, 
    debouncedSearchText, 
    statusFilter
  ], [selectedGroupId, currentPage, pageSize, debouncedSearchText, statusFilter]);

  // Memoized query functions - optimized to fetch only minimal fields
  const fetchGroups = useCallback(async () => {
    const response = await groupsAPI.getAll({ 
      fields: 'minimal',
      search: debouncedGroupSearchText || undefined,
      limit: debouncedGroupSearchText ? 100 : 1000 // Increased limit for better UX
    });
    return response.data;
  }, [debouncedGroupSearchText]);

  // Fetch comparison with pagination - server-side filtering
  const fetchComparison = useCallback(async () => {
    if (!selectedGroupId) return null;
    try {
      const response = await comparisonAPI.getComparison(selectedGroupId, {
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchText || undefined,
        status: statusFilter
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }, [selectedGroupId, currentPage, pageSize, debouncedSearchText, statusFilter]);

  // Fetch groups with TanStack Query - optimized for large datasets
  const { 
    data: groups = [], 
    isLoading: groupsLoading,
    isFetching: groupsFetching,
    error: groupsError 
  } = useQuery({
    queryKey: groupsQueryKey,
    queryFn: fetchGroups,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    placeholderData: (previousData) => previousData, // Prevent loading flicker
  });

  // Fetch comparison with TanStack Query - with pagination
  const { 
    data: comparison, 
    isLoading: comparisonLoading,
    isFetching: comparisonFetching,
    error: comparisonError,
    refetch: refetchComparison
  } = useQuery({
    queryKey: comparisonQueryKey,
    queryFn: fetchComparison,
    enabled: !!selectedGroupId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new page
  });

  const loading = groupsLoading || comparisonLoading;

  // Memoized handlers
  const handleFileUpload = useCallback(async (file: File) => {
    if (!selectedGroupId) {
      message.error('Please select a group first');
      return false;
    }

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      message.error('Please upload a CSV file');
      return false;
    }

    try {
      setUploading(true);
      await comparisonAPI.uploadCSV(selectedGroupId, file);
      message.success('CSV uploaded and analyzed successfully');
      // Invalidate all comparison queries for this group
      queryClient.invalidateQueries({ 
        queryKey: ['comparison', selectedGroupId],
        exact: false 
      });
      // Reset to first page
      setCurrentPage(1);
      return false; // Prevent default upload behavior
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to upload CSV');
      return false;
    } finally {
      setUploading(false);
    }
  }, [selectedGroupId, queryClient]);

  const handleReanalyze = useCallback(async () => {
    if (!selectedGroupId) {
      message.error('Please select a group first');
      return;
    }

    try {
      setReanalyzing(true);
      await comparisonAPI.reanalyze(selectedGroupId);
      message.success('Reanalysis completed successfully');
      // Invalidate all comparison queries for this group
      queryClient.invalidateQueries({ 
        queryKey: ['comparison', selectedGroupId],
        exact: false 
      });
      setCurrentPage(1);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to reanalyze');
    } finally {
      setReanalyzing(false);
    }
  }, [selectedGroupId, queryClient]);

  const handleDeleteComparison = useCallback(() => {
    if (!selectedGroupId) {
      return;
    }

    Modal.confirm({
      title: 'Delete Comparison Data',
      content: 'Are you sure you want to delete this comparison data? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await comparisonAPI.deleteComparison(selectedGroupId);
          message.success('Comparison data deleted successfully');
          // Invalidate all comparison queries for this group
          queryClient.invalidateQueries({ 
            queryKey: ['comparison', selectedGroupId],
            exact: false 
          });
          setCurrentPage(1);
        } catch (error: any) {
          message.error('Failed to delete comparison data');
        }
      },
    });
  }, [selectedGroupId, queryClient]);

  const handleGroupChange = useCallback((value: string) => {
    setSelectedGroupId(value);
    setCurrentPage(1);
    setSearchText('');
    setStatusFilter(undefined);
  }, []);

  const handleGroupSearch = useCallback((value: string) => {
    setGroupSearchText(value);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  }, [pageSize]);

  const handleStatusFilterChange = useCallback((value: 'present' | 'not_present' | undefined) => {
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page on filter change
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    // Create CSV template with headers and example data
    const csvContent = [
      ['phoneNumber', 'name'],
      ['+919876543210', 'John Doe'],
      ['+919876543211', 'Jane Smith'],
      ['+919876543212', ''],
      ['+919876543213', 'Bob Johnson'],
    ]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'comparison_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('Template downloaded successfully');
  }, []);

  // Memoized group options for Select - optimized to prevent re-renders
  const groupOptions = useMemo(() => {
    if (!groups || groups.length === 0) return [];
    
    return groups.map((group: any) => ({
      value: group._id,
      label: `${group.name}${group.accountId?.accountName ? ` (${group.accountId.accountName})` : ''}`,
      group: group,
    }));
  }, [groups]);

  // Memoized columns - prevent recreation on every render
  const columns = useMemo(() => [
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      sorter: (a: any, b: any) => a.phoneNumber.localeCompare(b.phoneNumber),
      width: 180,
      fixed: 'left' as const,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: any, b: any) => (a.name || '').localeCompare(b.name || ''),
      render: (text: string) => text || <Text type="secondary">-</Text>,
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Present', value: 'present' },
        { text: 'Not Present', value: 'not_present' },
      ],
      filteredValue: statusFilter ? [statusFilter] : null,
      onFilter: (value: any, record: any) => record.status === value,
      render: (status: string) => <StatusTag status={status as 'present' | 'not_present'} />,
      width: 150,
    },
  ], [statusFilter]);

  // Memoized pagination config - using server-side pagination
  const paginationConfig = useMemo(() => ({
    current: currentPage,
    pageSize: pageSize,
    total: comparison?.pagination?.total || comparison?.totalUploaded || 0,
    showSizeChanger: true,
    showTotal: (total: number, range: [number, number]) => 
      `Showing ${range[0]}-${range[1]} of ${total} numbers`,
    onChange: handlePageChange,
    onShowSizeChange: handlePageChange,
    pageSizeOptions: ['20', '50', '100', '200'],
    showQuickJumper: true,
  }), [currentPage, pageSize, comparison?.pagination?.total, comparison?.totalUploaded, handlePageChange]);

  // Memoized statistics cards to prevent unnecessary re-renders
  const statisticsCards = useMemo(() => {
    if (!comparison) return null;

    return (
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Uploaded"
              value={comparison.totalUploaded}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Present in Group"
              value={comparison.presentCount}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Not Present"
              value={comparison.notPresentCount}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Match Percentage"
              value={comparison.matchPercentage}
              suffix="%"
              valueStyle={{ color: comparison.matchPercentage >= 50 ? '#3f8600' : '#cf1322' }}
            />
            <Progress 
              percent={comparison.matchPercentage} 
              status={comparison.matchPercentage >= 50 ? 'success' : 'exception'}
              size="small"
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
      </Row>
    );
  }, [comparison]);

  return (
    <div className="comparison-page">
      <Title level={2}>Group Comparison & Analytics</Title>
      <Text type="secondary">Upload CSV files with phone numbers to compare against group members</Text>

      <Card style={{ marginTop: 24, marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Select Group:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Search and select a group to analyze"
              value={selectedGroupId || undefined}
              onChange={handleGroupChange}
              onSearch={handleGroupSearch}
              showSearch
              allowClear
              filterOption={false} // Disable client-side filtering, use server-side search
              loading={groupsLoading || groupsFetching}
              options={groupOptions}
              notFoundContent={groupsLoading ? <Spin size="small" /> : 'No groups found'}
              virtual={true} // Enable virtualization for large lists
              listHeight={400} // Limit dropdown height for better performance
              optionFilterProp="label"
            />
          </div>

          {selectedGroupId && (
            <Space wrap>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
              <Upload
                accept=".csv"
                beforeUpload={handleFileUpload}
                showUploadList={false}
                disabled={uploading}
              >
                <Button 
                  type="primary" 
                  icon={<UploadOutlined />} 
                  loading={uploading}
                  disabled={!selectedGroupId}
                >
                  {comparison ? 'Upload New CSV' : 'Upload CSV'}
                </Button>
              </Upload>

              {comparison && (
                <>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={handleReanalyze}
                    loading={reanalyzing}
                  >
                    Reanalyze
                  </Button>
                  <Button 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={handleDeleteComparison}
                  >
                    Delete Data
                  </Button>
                </>
              )}
            </Space>
          )}
        </Space>
      </Card>

      {loading && !comparison && groups.length === 0 && <Loader />}

      {comparison && (
        <>
          {statisticsCards}

          <Card>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong>Group: </Text>
                <Text>{comparison.groupId.name}</Text>
                {comparison.groupId.description && (
                  <>
                    <Text type="secondary"> - </Text>
                    <Text type="secondary">{comparison.groupId.description}</Text>
                  </>
                )}
              </div>
              <div>
                <Text strong>Account: </Text>
                <Text>{comparison.accountId.accountName}</Text>
              </div>
              <div>
                <Text strong>Last Analyzed: </Text>
                <Text>{new Date(comparison.lastAnalyzed).toLocaleString()}</Text>
              </div>
              {comparison.fileName && (
                <div>
                  <Text strong>File: </Text>
                  <Text>{comparison.fileName}</Text>
                </div>
              )}

              <Space wrap>
                <Search
                  placeholder="Search by phone number or name"
                  allowClear
                  value={searchText}
                  onChange={handleSearchChange}
                  onPressEnter={(e) => {
                    const target = e.target as HTMLInputElement;
                    setSearchText(target.value);
                    setCurrentPage(1);
                  }}
                  style={{ width: 300 }}
                />
                <Select
                  placeholder="Filter by status"
                  allowClear
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  style={{ width: 200 }}
                >
                  <Select.Option value="present">Present</Select.Option>
                  <Select.Option value="not_present">Not Present</Select.Option>
                </Select>
              </Space>

              <Table
                dataSource={comparison.uploadedNumbers || []}
                columns={columns}
                rowKey={(record, index) => `${record.phoneNumber}-${index}`}
                loading={comparisonLoading || comparisonFetching}
                pagination={paginationConfig}
                scroll={{ x: 'max-content', y: 600 }} // Virtual scrolling for better performance
                size="small" // Smaller row height for better performance
                bordered
              />
            </Space>
          </Card>
        </>
      )}

      {!loading && !comparison && selectedGroupId && (
        <Card>
          <Alert
            message="No Comparison Data"
            description="Upload a CSV file to start comparing phone numbers with group members."
            type="info"
            showIcon
            icon={<FileTextOutlined />}
          />
        </Card>
      )}

      {!selectedGroupId && (
        <Card>
          <Alert
            message="Select a Group"
            description="Please select a group from the dropdown above to upload and analyze CSV files."
            type="info"
            showIcon
          />
        </Card>
      )}
    </div>
  );
};

export default memo(Comparison);
